import React, { useState } from "react";
import { Button, Input, message } from "antd";
import { API } from "aws-amplify";

const AISuggestionsPanel = ({
  showSuggestPanel,
  onToggle,
  businessName,
  onAddKeyword,
  existingServices,
}) => {
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [keywordSuggestions, setKeywordSuggestions] = useState([]);
  const [extraPrompt, setExtraPrompt] = useState("");

  const fetchKeywordSuggestions = async () => {
    try {
      setSuggestLoading(true);
      setKeywordSuggestions([]);

      const query = new URLSearchParams({
        businessName: businessName || "",
        extraPrompt,
      }).toString();

      const res = await API.get("REST", `/publish/suggest-keywords?${query}`);
      const kws = res?.keywords || [];
      setKeywordSuggestions(kws);
    } catch (err) {
      message.error("Failed to fetch suggestions");
    } finally {
      setSuggestLoading(false);
    }
  };

  const addAllSuggestions = () => {
    const toAdd = keywordSuggestions.filter((k) => !isKeywordAdded(k));
    if (toAdd.length) {
      toAdd.forEach((keyword) => onAddKeyword(keyword));
    }
  };

  const isKeywordAdded = (keyword) => {
    if (!existingServices || !Array.isArray(existingServices)) return false;
    const normalizedKeyword = String(keyword).trim().toLowerCase();
    return existingServices.some((service) => {
      const normalizedService = String(service).trim().toLowerCase();
      return normalizedService === normalizedKeyword;
    });
  };

  if (!showSuggestPanel) return null;

  return (
    <div className="mt-2 mb-4 border border-gray-200 rounded-xl p-3">
      <div className="flex items-center justify-end mb-2">
        <div className="flex items-center justify-end gap-2">
          <Button
            size="small"
            className="rounded-md"
            onClick={() => setKeywordSuggestions([])}>
            Clear
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2">
        <Input
          size="middle"
          placeholder="Tell us about your business"
          value={extraPrompt}
          className="input-border"
          onChange={(e) => setExtraPrompt(e.target.value)}
        />
        <Button
          type="primary"
          className="rounded-lg"
          loading={suggestLoading}
          onClick={fetchKeywordSuggestions}>
          Suggest
        </Button>
      </div>

      <div className="mt-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-medium text-gray-700">Suggestions</div>
          {keywordSuggestions.length > 0 && (
            <button
              type="button"
              className="text-blue-600 hover:text-blue-700 text-sm"
              onClick={addAllSuggestions}>
              Add All
            </button>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          {keywordSuggestions.length === 0 && !suggestLoading ? (
            <span className="text-gray-500 text-sm">No suggestions yet</span>
          ) : (
            keywordSuggestions.map((keyword) => {
              const isAdded = isKeywordAdded(keyword);
              return (
                <span
                  key={keyword}
                  title={isAdded ? "Already added" : "Add this service"}
                  className={`px-2 py-1 rounded-lg text-sm select-none inline-flex items-center gap-1 ${
                    isAdded ? "bg-green-100 text-green-700" : "bg-gray-100"
                  }`}>
                  <span>{keyword}</span>
                  {!isAdded ? (
                    <button
                      type="button"
                      className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-600 text-white hover:bg-blue-700"
                      onClick={() => onAddKeyword(keyword)}
                      title="Add">
                      +
                    </button>
                  ) : (
                    <span className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-600 text-white text-xs font-bold">
                      ✓
                    </span>
                  )}
                </span>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default AISuggestionsPanel;
